// Slice B1: returns a stable callback that opens the relevant accordion
// (if closed) and then focuses the underlying input for a checklist
// item. Used by `ChecklistCard` so a missing-field row becomes
// click-to-fix.
//
// Two-step pattern: flipping a `useState` setter is synchronous but the
// DOM render isn't, so we schedule the focus call on the next animation
// frame to give React a chance to mount the input. The
// `scrollIntoViewAndFocus` helper then handles its own scroll-then-focus
// delay.
//
// See `docs/planning/lead_control_mvp_polish.md` for the slice context.

import { useCallback, type Dispatch, type SetStateAction } from "react";
import type { LeadChecklistItemKey } from "./leadChecklistItems";
import {
  isLocationAccordionKey,
  isProductAccordionKey,
  scrollIntoViewAndFocus,
  selectorForChecklistKey,
} from "@/lib/lead/leadFieldFocus";

export type UseChecklistFieldFocusArgs = {
  showProductDetails: boolean;
  setShowProductDetails: Dispatch<SetStateAction<boolean>>;
  showLocationDetails: boolean;
  setShowLocationDetails: Dispatch<SetStateAction<boolean>>;
  /**
   * Optional hook called before focusing — used by future surfaces
   * (e.g. the dock-level checklist) to flip the active mode to `leads`
   * first so the input is mounted in the DOM. Returning a non-zero
   * number adds that many extra milliseconds before the focus, on top
   * of the next-frame schedule that handles the accordion render.
   */
  onBeforeFocus?: () => number | void;
};

/**
 * Returns a callback `(key) => void` that orchestrates the click-to-focus
 * behavior. Stable across renders so consumers can put it on JSX click
 * handlers without re-rendering the row.
 */
export function useChecklistFieldFocus(args: UseChecklistFieldFocusArgs) {
  const {
    showProductDetails,
    setShowProductDetails,
    showLocationDetails,
    setShowLocationDetails,
    onBeforeFocus,
  } = args;

  return useCallback(
    (key: LeadChecklistItemKey) => {
      const extraDelayMs = onBeforeFocus?.() ?? 0;

      // Flip the right accordion BEFORE we kick off the scroll/focus so
      // the input is in the DOM by the time the rAF callback fires.
      // Idempotent — flipping `true → true` is a no-op for React state.
      if (isProductAccordionKey(key) && !showProductDetails) {
        setShowProductDetails(true);
      }
      if (isLocationAccordionKey(key) && !showLocationDetails) {
        setShowLocationDetails(true);
      }

      const selector = selectorForChecklistKey(key);

      const runFocus = () => {
        scrollIntoViewAndFocus(selector);
      };

      if (typeof window === "undefined") {
        runFocus();
        return;
      }

      // Wait one frame for the accordion render, then optionally a few
      // ms more if the parent surface needed to switch modes / hydrate.
      window.requestAnimationFrame(() => {
        if (typeof extraDelayMs === "number" && extraDelayMs > 0) {
          window.setTimeout(runFocus, extraDelayMs);
        } else {
          runFocus();
        }
      });
    },
    [
      onBeforeFocus,
      setShowLocationDetails,
      setShowProductDetails,
      showLocationDetails,
      showProductDetails,
    ],
  );
}
